import { useState, useEffect } from 'react';
import { User } from '../hooks/useAuth';
import SantriTable from './SantriTable';
import StatsCard from './StatsCard';

interface DashboardProps {
  user: User;
}

interface Santri {
  id: string;
  nama: string;
  nis?: string;
  kelompok: {
    id: string;
    nama: string;
    desa: { id: string; nama: string };
  };
  fotoKey?: string;
  createdAt: string;
}

export default function Dashboard({ user }: DashboardProps) {
  const [santri, setSantri] = useState<Santri[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadSantri();
  }, []);

  const loadSantri = async () => {
    try {
      const res = await fetch('/api/santri', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setSantri(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load santri:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Yakin ingin menghapus santri ini?')) return;

    try {
      const res = await fetch(`/api/santri/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (res.ok) {
        loadSantri();
      }
    } catch (error) {
      console.error('Failed to delete santri:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Total Santri"
          value={santri.length}
          icon="📚"
          color="primary"
        />
        <StatsCard
          title="Kelompok"
          value={user.role === 'KELOMPOK' ? 1 : 'N/A'}
          icon="👥"
          color="blue"
        />
        <StatsCard
          title="Role Akses"
          value={user.role}
          icon="🔐"
          color="purple"
        />
      </div>

      {/* Santri Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Data Santri</h2>
          {user.role === 'DAERAH' && (
            <button
              className="btn-primary flex items-center space-x-2"
              onClick={() => setShowAddModal(true)}
            >
              <span>+</span>
              <span>Tambah Santri</span>
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <SantriTable
            santri={santri}
            onDelete={handleDelete}
            canEdit={user.role === 'DAERAH'}
          />
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddSantriModal
          onClose={() => setShowAddModal(false)}
          onAdded={loadSantri}
        />
      )}
    </div>
  );
}

function AddSantriModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
    nis: '',
    kelompokId: '',
    tempatLahir: '',
    tanggalLahir: '',
    alamat: '',
    namaWali: '',
    noTeleponWali: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch('/api/santri', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        onAdded();
        onClose();
      }
    } catch (error) {
      console.error('Failed to add santri:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-100 rounded-xl border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800">
          <h3 className="text-lg font-semibold text-white">Tambah Santri Baru</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nama Lengkap *
              </label>
              <input
                className="input"
                value={formData.nama}
                onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                NIS
              </label>
              <input
                className="input"
                value={formData.nis}
                onChange={(e) => setFormData({ ...formData, nis: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tempat Lahir
              </label>
              <input
                className="input"
                value={formData.tempatLahir}
                onChange={(e) => setFormData({ ...formData, tempatLahir: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tanggal Lahir
              </label>
              <input
                type="date"
                className="input"
                value={formData.tanggalLahir}
                onChange={(e) => setFormData({ ...formData, tanggalLahir: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Alamat
            </label>
            <textarea
              className="input"
              rows={2}
              value={formData.alamat}
              onChange={(e) => setFormData({ ...formData, alamat: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nama Wali
              </label>
              <input
                className="input"
                value={formData.namaWali}
                onChange={(e) => setFormData({ ...formData, namaWali: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                No. Telepon Wali
              </label>
              <input
                className="input"
                value={formData.noTeleponWali}
                onChange={(e) => setFormData({ ...formData, noTeleponWali: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-800">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Batal
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
